import { Injectable } from '@angular/core';
import { Addon } from '@galaxy/marketplace-apps/v1';
import {
  AddonKey,
  GetMultiAddonsRequest,
  GetMultiAddonsResponse,
  MarketplacePackagesApiService,
} from '@vendasta/marketplace-packages';
import { BehaviorSubject, combineLatest, forkJoin, Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES } from '../constants';
import { MarketplaceAppService } from './marketplace-app.service';

const ADDON_PREFIX = 'A-';

export interface AddonsMap {
  [productId: string]: Addon[];
}

@Injectable({ providedIn: 'root' })
export class AddonsService {
  private addonsMapSubject: BehaviorSubject<AddonsMap> = new BehaviorSubject({});

  constructor(
    private marketplacePackagesApiService: MarketplacePackagesApiService,
    private marketplaceAppService: MarketplaceAppService,
  ) {}

  /* tslint:disable:no-string-literal */
  getAddons(appId: string, partnerId: string): Observable<Addon[]> {
    if (!Object.prototype.hasOwnProperty.call(this.addonsMapSubject.getValue(), appId)) {
      this.loadAddonsForProduct(appId, partnerId).subscribe((addons) => {
        this.setAddOnsForProduct(appId, addons);
      });
    }
    return this.addonsMapSubject.pipe(map((addonsMap) => addonsMap[appId]));
  }

  getAddon(appId: string, addonId: string): Observable<Addon> {
    const request = new GetMultiAddonsRequest();
    const addonKey = new AddonKey();
    addonKey.addonId = addonId;
    addonKey.appId = appId;
    request.addonKeys = [addonKey];
    request.whitelabelOverrideKey = GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES;
    return this.marketplacePackagesApiService
      .getMultiAddons(request)
      .pipe(
        map((resp: GetMultiAddonsResponse) => (resp.addons && resp.addons.length ? new Addon(resp.addons[0]) : null)),
      );
  }

  private setAddOnsForProduct(appId: string, addons: Addon[]): void {
    const addonsMap = this.addonsMapSubject.getValue();
    addonsMap[appId] = addons;
    this.addonsMapSubject.next(addonsMap);
  }

  private loadAddonsForProduct(appId: string, partnerId: string): Observable<Addon[]> {
    return this.listAddonsMulti([appId], partnerId);
  }

  public listAddonsMulti(appIds: string[], partnerId: string): Observable<Addon[]> {
    const pageSize = 200;

    const apps$ = this.marketplaceAppService.apps$;

    const appLookup = new Set(appIds);

    const filteredApps$ = apps$.pipe(
      map((apps) => {
        return apps.filter((app) => appLookup.has(app.appId));
      }),
    );

    const childKeys$: Observable<AddonKey[]> = filteredApps$.pipe(
      map((apps) => {
        const addons = apps.map((app) => {
          const children = app?.restrictions?.appRequirement?.childrenAppIds || [];
          return children
            .filter((id) => id.startsWith(ADDON_PREFIX))
            .map((id) => new AddonKey({ addonId: id, appId: app.appId }));
        });
        return addons.flat();
      }),
    );

    // filter out children that partner doesn't have access to
    const accessibleChildren$ = childKeys$.pipe(
      switchMap((childKeys) => {
        const appIds = childKeys.map((key) => key.addonId);
        return this.marketplacePackagesApiService.areAppsDistributedToPartner({ partnerId, appIds });
      }),
      map((response) => response.areAppsDistributed),
    );

    return combineLatest([childKeys$, accessibleChildren$]).pipe(
      map(([keys, distributed]) => {
        return keys.filter((key) => {
          const dist = distributed.find((dist) => dist.appId === key.addonId);
          return dist.isDistributed;
        });
      }),
      switchMap((childKeys) => {
        const pages = childKeys.reduce(chunk(pageSize), []);
        return forkJoin(
          pages.map((page) => {
            const addonsRequest = new GetMultiAddonsRequest();
            addonsRequest.addonKeys = page;
            addonsRequest.whitelabelOverrideKey = GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES;
            return this.marketplacePackagesApiService.getMultiAddons(addonsRequest).pipe(
              catchError(() => observableThrowError('Something went wrong getting addons')),
              map((response) => response.addons),
            );
          }),
        ).pipe(map((pagedAddons) => pagedAddons.flat() as Addon[]));
      }),
    );
  }

  getMultiAddons(ak: AddonKey[]): Observable<Addon[]> {
    const request = new GetMultiAddonsRequest();
    const addonKeys = new Array<AddonKey>();
    ak.map((key: any) => {
      addonKeys.push(
        new AddonKey({
          appId: key.app_id || key.appId,
          addonId: key.addon_id || key.addonId,
          quantity: key.quantity,
        }),
      );
    });
    request.addonKeys = addonKeys;
    request.whitelabelOverrideKey = GET_VENDOR_APP_NAMES_INSTEAD_OF_WHITELABELED_NAMES;
    const addonResponse$: Observable<GetMultiAddonsResponse> = this.marketplacePackagesApiService
      .getMultiAddons(request)
      .pipe(catchError(() => observableThrowError('Something went wrong getting addons')));
    return addonResponse$.pipe(
      map((response) => (response.addons ? response.addons.map((addon) => new Addon(addon)) : [])),
    );
  }
}

function chunk<U, T extends Array<U>>(
  pageSize: number,
): (previousValue: T[], currentValue: U, currentIndex: number) => T[] {
  return (resultArray: T[], item: U, index: number) => {
    const chunkIndex = Math.floor(index / pageSize);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = <T>[];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  };
}
